import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Calendar as CalendarLucide, ChevronLeft, ChevronRight, X } from 'lucide-react'

export interface DateRange {
    start: Date | null
    end: Date | null
}

export interface DateRangePickerProps {
    variant?: 'primary' | 'secondary'
    label?: string
    description?: string
    error?: string
    /** Shown in the trigger instead of the mm / dd / yyyy placeholders while no range is picked. */
    emptyLabel?: string
    value?: DateRange
    defaultValue?: DateRange
    onChange?: (range: DateRange) => void
    disabled?: boolean
    required?: boolean
    showTime?: boolean
    /** Shows a cross in the trigger while a date is picked; it resets both ends and reports `{ start: null, end: null }`. */
    clearable?: boolean
    /** Reports the calendar opening and closing, for a host that has to make room for it (a modal). */
    onOpenChange?: (open: boolean) => void
    minDate?: Date
    maxDate?: Date
    style?: React.CSSProperties
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const formatInput = (date: Date | null): string => {
    if (!date) return 'mm / dd / yyyy'
    return `${String(date.getMonth() + 1).padStart(2, '0')} / ${String(date.getDate()).padStart(2, '0')} / ${date.getFullYear()}`
}

const formatSummary = (date: Date): string => {
    const d = date.getDate()
    const m = date.toLocaleString('en-US', { month: 'short' })
    const y = date.getFullYear()
    return `${d} ${m}, ${y}`
}

const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const isBetween = (date: Date, start: Date, end: Date) => {
    const d = new Date(date).setHours(0, 0, 0, 0)
    return d > new Date(start).setHours(0, 0, 0, 0) && d < new Date(end).setHours(0, 0, 0, 0)
}

const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()
    const days: { date: Date; current: boolean }[] = []
    for (let i = firstDay - 1; i >= 0; i--) days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), current: false })
    for (let i = 1; i <= daysInMonth; i++) days.push({ date: new Date(year, month, i), current: true })
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) days.push({ date: new Date(year, month + 1, i), current: false })
    return days
}

interface TimeValue {
    hours: number
    minutes: number
    period: 'AM' | 'PM'
}

const TimeSegment = ({ value, max, onChange }: { value: number; max: number; onChange: (v: number) => void }) => {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState('')
    const inputRef = useRef<HTMLInputElement>(null)

    const commit = (raw: string) => {
        const n = parseInt(raw, 10)
        const min = max === 12 ? 1 : 0
        if (!isNaN(n) && n >= min && n <= max) onChange(n)
        setEditing(false)
        setDraft('')
    }

    if (editing) {
        return (
            <input
                ref={inputRef}
                value={draft}
                autoFocus
                onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 2)
                    setDraft(v)
                    if (v.length === 2) commit(v)
                }}
                onBlur={() => commit(draft)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') commit(draft)
                    if (e.key === 'Escape') {
                        setEditing(false)
                        setDraft('')
                    }
                }}
                className="w-[22px] rounded-sm border-none bg-accent-soft px-0.5 py-px text-center text-sm text-foreground outline-none"
            />
        )
    }

    return (
        <button
            type="button"
            onClick={() => {
                setEditing(true)
                setDraft('')
            }}
            className="cursor-text rounded-sm border-none bg-transparent px-0.5 py-px text-sm leading-5 text-foreground transition-colors duration-100 hover:bg-accent-soft"
        >
            {String(value).padStart(2, '0')}
        </button>
    )
}

const TimePill = ({ label, value, onChange }: { label: string; value: TimeValue; onChange: (v: TimeValue) => void }) => {
    return (
        <div className="flex items-center justify-between pt-3">
            <span className="text-sm font-medium text-foreground">{label}</span>
            <div className="inline-flex min-h-8 items-center gap-px rounded-2xl bg-default px-3 py-1 text-sm font-normal leading-5 text-foreground">
                <TimeSegment value={value.hours} max={12} onChange={(h) => onChange({ ...value, hours: h })} />
                <span className="select-none text-foreground-muted">:</span>
                <TimeSegment value={value.minutes} max={59} onChange={(m) => onChange({ ...value, minutes: m })} />
                <button
                    type="button"
                    onClick={() => onChange({ ...value, period: value.period === 'AM' ? 'PM' : 'AM' })}
                    className="ml-0.5 cursor-pointer rounded-sm border-none bg-transparent px-1 py-px text-sm leading-5 text-foreground transition-colors duration-100 hover:bg-accent-soft"
                >
                    {value.period}
                </button>
                <span className="ml-0.5 select-none text-xs text-foreground-muted">EDT</span>
            </div>
        </div>
    )
}

const PANEL_GAP = 8

const navButtonClass = 'flex size-7 cursor-pointer items-center justify-center rounded-md text-foreground'

export const DateRangePicker = ({
    variant = 'primary',
    label,
    description,
    emptyLabel,
    error,
    value: controlledValue,
    defaultValue = { start: null, end: null },
    onChange,
    disabled = false,
    required = false,
    showTime = false,
    clearable = false,
    onOpenChange,
    minDate,
    maxDate,
    style,
}: DateRangePickerProps) => {
    const [internalValue, setInternalValue] = useState<DateRange>(defaultValue)
    const range = controlledValue !== undefined ? controlledValue : internalValue
    const [open, setOpen] = useState(false)
    const [calendarView, setCalendarView] = useState<'days' | 'months'>('days')
    const [selecting, setSelecting] = useState<'start' | 'end'>('start')
    const [startTime, setStartTime] = useState<TimeValue>({ hours: 10, minutes: 30, period: 'AM' })
    const [endTime, setEndTime] = useState<TimeValue>({ hours: 10, minutes: 30, period: 'AM' })
    const containerRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const [openUp, setOpenUp] = useState(false)
    const hasError = !!error
    const isSecondary = variant === 'secondary'
    const today = useMemo(() => new Date(), [])

    const [viewYear, setViewYear] = useState((range.start || today).getFullYear())
    const [viewMonth, setViewMonth] = useState((range.start || today).getMonth())

    const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])
    const monthLabel = new Date(viewYear, viewMonth).toLocaleString('en-US', { month: 'long', year: 'numeric' })

    const goToPrev = useCallback(() => {
        if (viewMonth === 0) {
            setViewMonth(11)
            setViewYear((y) => y - 1)
        } else setViewMonth((m) => m - 1)
    }, [viewMonth])

    const goToNext = useCallback(() => {
        if (viewMonth === 11) {
            setViewMonth(0)
            setViewYear((y) => y + 1)
        } else setViewMonth((m) => m + 1)
    }, [viewMonth])

    const handleDayClick = useCallback(
        (date: Date) => {
            let next: DateRange
            if (selecting === 'start') {
                next = { start: date, end: range.end && date > range.end ? null : range.end }
                setSelecting('end')
            } else {
                next = range.start && date < range.start ? { start: date, end: range.start } : { start: range.start, end: date }
                setSelecting('start')
            }
            if (controlledValue === undefined) setInternalValue(next)
            onChange?.(next)
        },
        [selecting, range, controlledValue, onChange]
    )

    useEffect(() => {
        onOpenChange?.(open)
    }, [open, onOpenChange])

    const canClear = clearable && !disabled && (!!range.start || !!range.end)

    const handleClear = useCallback(() => {
        const next: DateRange = { start: null, end: null }
        setSelecting('start')
        if (controlledValue === undefined) setInternalValue(next)
        onChange?.(next)
    }, [controlledValue, onChange])

    // Opens below the trigger, flipping above it only when the panel would run off the bottom of the window and
    // it fits above. Measured before paint so it never shows in the wrong place first, and again when the window
    // is resized or scrolled while it is open.
    const placePanel = useCallback(() => {
        if (!containerRef.current || !panelRef.current) return
        const trigger = containerRef.current.getBoundingClientRect()
        const needed = panelRef.current.offsetHeight + PANEL_GAP
        setOpenUp(needed > window.innerHeight - trigger.bottom && needed <= trigger.top)
    }, [])

    useLayoutEffect(() => {
        if (open) placePanel()
    }, [open, calendarView, range.start, range.end, showTime, placePanel])

    useEffect(() => {
        if (!open) return
        window.addEventListener('resize', placePanel)
        window.addEventListener('scroll', placePanel, true)
        return () => {
            window.removeEventListener('resize', placePanel)
            window.removeEventListener('scroll', placePanel, true)
        }
    }, [open, placePanel])

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const triggerStateClasses = open ? 'ring-2 ring-accent' : hasError ? 'ring-1 ring-danger' : isSecondary ? '' : 'shadow-field'

    return (
        <div ref={containerRef} className={`relative flex flex-col gap-1 ${disabled ? 'opacity-50' : ''}`} style={style}>
            {label && (
                <label className={`text-sm font-medium ${hasError ? 'text-danger' : 'text-foreground'}`}>
                    {label}
                    {required && <span className="ml-0.5 text-danger">*</span>}
                </label>
            )}

            {/* Trigger input */}
            <div className="relative">
                <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                        if (!disabled) setOpen((v) => !v)
                    }}
                    className={`flex h-9 w-full cursor-pointer items-center rounded-xl border-none p-0 text-left transition-shadow duration-150 disabled:cursor-not-allowed ${
                        isSecondary ? 'bg-default' : 'bg-surface'
                    } ${triggerStateClasses}`}
                >
                    <span className={`flex flex-1 items-center gap-1 pl-3 text-sm font-normal leading-5 ${clearable ? 'pr-8' : 'pr-3'}`}>
                        {emptyLabel && !range.start && !range.end ? (
                            <span className="text-foreground-muted">{emptyLabel}</span>
                        ) : (
                            <>
                                <span className={range.start ? 'text-foreground' : 'text-foreground-muted'}>{formatInput(range.start)}</span>
                                <span className="text-foreground-muted">–</span>
                                <span className={range.end ? 'text-foreground' : 'text-foreground-muted'}>{formatInput(range.end)}</span>
                            </>
                        )}
                    </span>
                    <span className="flex items-center px-3 text-foreground-muted">
                        <CalendarLucide size={16} strokeWidth={1.5} />
                    </span>
                </button>

                {canClear && (
                    <button
                        type="button"
                        aria-label="Clear dates"
                        onClick={handleClear}
                        className="absolute right-10 top-1/2 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-foreground-muted transition-colors duration-150 hover:bg-accent-soft hover:text-foreground"
                    >
                        <X size={14} strokeWidth={1.5} />
                    </button>
                )}
            </div>

            {!hasError && description && <span className="text-xs text-foreground-muted">{description}</span>}
            {hasError && <span className="text-xs text-danger">{error}</span>}

            {/* Calendar panel */}
            {open && (
                <div
                    ref={panelRef}
                    className={`absolute left-0 z-50 box-border w-[280px] rounded-3xl bg-surface p-4 shadow-overlay backdrop-blur-[20px] ${
                        openUp ? 'bottom-full mb-2' : 'top-full mt-2'
                    }`}
                >
                    {/* Month nav */}
                    <div className="flex items-center justify-between pb-2">
                        {calendarView === 'days' ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setCalendarView('months')}
                                    className="flex cursor-pointer items-center gap-1 text-sm font-medium text-foreground"
                                >
                                    {monthLabel}
                                    <ChevronRight size={16} strokeWidth={1.5} />
                                </button>
                                <div className="flex gap-0.5">
                                    <button type="button" onClick={goToPrev} className={navButtonClass}>
                                        <ChevronLeft size={16} strokeWidth={1.5} />
                                    </button>
                                    <button type="button" onClick={goToNext} className={navButtonClass}>
                                        <ChevronRight size={16} strokeWidth={1.5} />
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex w-full items-center justify-between">
                                <button type="button" onClick={() => setViewYear((y) => y - 1)} className={navButtonClass}>
                                    <ChevronLeft size={16} strokeWidth={1.5} />
                                </button>
                                <button type="button" onClick={() => setCalendarView('days')} className="cursor-pointer text-sm font-medium text-foreground">
                                    {viewYear}
                                </button>
                                <button type="button" onClick={() => setViewYear((y) => y + 1)} className={navButtonClass}>
                                    <ChevronRight size={16} strokeWidth={1.5} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Month picker view */}
                    {calendarView === 'months' && (
                        <div className="grid grid-cols-3 gap-1 pb-2">
                            {Array.from({ length: 12 }, (_, i) => {
                                const mLabel = new Date(viewYear, i).toLocaleString('en-US', { month: 'short' })
                                const isActive = i === viewMonth
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => {
                                            setViewMonth(i)
                                            setCalendarView('days')
                                        }}
                                        className={`flex h-9 cursor-pointer items-center justify-center rounded-[20px] text-sm transition-colors duration-150 ${
                                            isActive ? 'bg-accent font-semibold text-accent-foreground' : 'bg-default font-normal text-foreground'
                                        }`}
                                    >
                                        {mLabel}
                                    </button>
                                )
                            })}
                        </div>
                    )}

                    {/* Day grid (only in days view) */}
                    {calendarView === 'days' && (
                        <>
                            {/* Day headers */}
                            <div className="mb-1 grid grid-cols-7">
                                {DAYS.map((d) => (
                                    <div key={d} className="flex items-center justify-center py-2 text-sm font-medium text-foreground-muted">
                                        {d}
                                    </div>
                                ))}
                            </div>

                            {/* Day grid */}
                            <div className="grid grid-cols-7">
                                {days.map(({ date, current }, i) => {
                                    const col = i % 7
                                    const isStart = range.start ? isSameDay(date, range.start) : false
                                    const isEnd = range.end ? isSameDay(date, range.end) : false
                                    const isInRange = range.start && range.end ? isBetween(date, range.start, range.end) : false
                                    const isToday = isSameDay(date, today)
                                    const isDisabled = !!((minDate && date < minDate) || (maxDate && date > maxDate))
                                    const isSelected = isStart || isEnd

                                    const showStrip =
                                        (isStart && range.end && !isSameDay(range.start!, range.end!)) ||
                                        (isEnd && range.start && !isSameDay(range.start!, range.end!)) ||
                                        isInRange
                                    const stripLeft = isStart ? '50%' : '0'
                                    const stripRight = isEnd ? '50%' : '0'
                                    const stripRadius = isInRange
                                        ? col === 0
                                            ? '8px 0 0 8px'
                                            : col === 6
                                              ? '0 8px 8px 0'
                                              : '0'
                                        : isStart
                                          ? col === 6
                                              ? '0 8px 8px 0'
                                              : '0'
                                          : isEnd
                                            ? col === 0
                                                ? '8px 0 0 8px'
                                                : '0'
                                            : '0'

                                    return (
                                        <div key={i} className="relative">
                                            {/* In-range strip */}
                                            {showStrip && (
                                                <div
                                                    className="pointer-events-none absolute inset-y-0 bg-accent-soft"
                                                    style={{ left: stripLeft, right: stripRight, borderRadius: stripRadius }}
                                                />
                                            )}

                                            {/* Day button */}
                                            <button
                                                type="button"
                                                disabled={isDisabled}
                                                onClick={() => !isDisabled && handleDayClick(date)}
                                                className={`relative flex aspect-square w-full cursor-pointer items-center justify-center rounded-3xl p-0 text-sm transition-colors duration-150 disabled:cursor-default disabled:opacity-50 ${
                                                    isSelected
                                                        ? 'bg-accent font-medium text-accent-foreground'
                                                        : current
                                                          ? 'font-normal text-foreground'
                                                          : 'font-normal text-foreground-muted'
                                                }`}
                                            >
                                                {date.getDate()}

                                                {/* Today dot */}
                                                {isToday && (
                                                    <span
                                                        className={`pointer-events-none absolute bottom-[3px] left-1/2 size-[3px] -translate-x-1/2 rounded-full ${
                                                            isSelected ? 'bg-accent-foreground' : 'bg-foreground-muted'
                                                        }`}
                                                    />
                                                )}
                                            </button>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Time rows */}
                            {showTime && (
                                <>
                                    <TimePill label="Start time" value={startTime} onChange={setStartTime} />
                                    <TimePill label="End time" value={endTime} onChange={setEndTime} />
                                </>
                            )}

                            {/* Selected summary */}
                            {(range.start || range.end) && (
                                <div className="flex items-center gap-1 pt-4 text-xs font-medium">
                                    <span className="text-foreground-muted">Selected:</span>
                                    <span className="text-foreground-muted">{range.start ? formatSummary(range.start) : '—'}</span>
                                    <span className="text-foreground-muted">–</span>
                                    <span className="font-semibold text-foreground">{range.end ? formatSummary(range.end) : '—'}</span>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    )
}
